import React from 'react';
import { CalendarDays, LogIn, Eye, Search, ShieldCheck, GraduationCap, Route, Bell, BarChart3 } from 'lucide-react';
import { useReveal } from '../../hooks/useReveal';
import DashboardMockup from '../DashboardMockup';
import { pastel } from '../../lib/palette';
import { openDemoRequest } from '../../lib/demoRequest';
import { goToApp } from '../../lib/smartAccess';

const pillars = [
  { icon: Eye, label: 'Observamos', tone: pastel.sky },
  { icon: Search, label: 'Detectamos', tone: pastel.amber },
  { icon: ShieldCheck, label: 'Prevenimos', tone: pastel.violet },
  { icon: GraduationCap, label: 'Protegemos', tone: pastel.mint },
];

// 3 valores potentes y diferenciadores
const valueProps = [
  { icon: Route, label: 'Trazabilidad real', tone: pastel.violet },
  { icon: Bell, label: 'Alertas oportunas', tone: pastel.amber },
  { icon: BarChart3, label: 'Decisiones basadas en datos', tone: pastel.teal },
];

const Hero = () => {
  const a = useReveal();
  const b = useReveal();

  return (
    <section id="inicio" data-testid="hero-section" className="relative overflow-hidden pt-24 sm:pt-32 pb-10 sm:pb-16">
      {/* fondo decorativo */}
      <div className="absolute inset-0 bg-soft -z-10" />
      <div className="absolute inset-0 bg-grid -z-10" />
      <div className="glow-violet -z-10" style={{ top: '-120px', left: '-160px' }} />
      <div className="glow-blue -z-10" style={{ top: '60px', right: '-180px' }} />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div ref={a.ref} className="reveal">
          <div className="chip" data-testid="hero-chip">
            <span className="w-2 h-2 rounded-full bg-brand-violet inline-block" />
            Inteligencia Preventiva Escolar
          </div>

          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-brand-deep leading-[1.05] tracking-tight">
            Observar hoy,{' '}
            <span className="text-brand-violet">prevenir mañana,</span>{' '}
            <span className="block">proteger siempre.</span>
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-brand-muted max-w-xl leading-relaxed">
            PreventiApp es la plataforma SaaS que ayuda a los colegios a fortalecer la
            convivencia escolar con <strong className="text-brand-deep">trazabilidad</strong>,{' '}
            <strong className="text-brand-deep">alertas oportunas</strong> y{' '}
            <strong className="text-brand-deep">apoyo a la toma de decisiones</strong>.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              data-testid="hero-cta-demo"
              onClick={() => openDemoRequest('hero')}
              className="btn-primary px-6 py-4 rounded-full font-semibold text-base inline-flex items-center justify-center gap-2"
            >
              <CalendarDays className="w-5 h-5" />
              Solicitar Demo
            </button>
            <button
              type="button"
              data-testid="hero-cta-acceder"
              onClick={() => goToApp('hero')}
              className="px-6 py-4 rounded-full font-bold text-base inline-flex items-center justify-center gap-2 text-white transition-all hover:brightness-110 hover:-translate-y-[1px]"
              style={{ background: '#0B1B5E', boxShadow: '0 14px 28px -14px rgba(11,27,94,0.6)' }}
            >
              <LogIn className="w-5 h-5" />
              ACCEDER
            </button>
          </div>

          {/* 3 propuestas de valor potentes */}
          <ul data-testid="hero-value-props" className="mt-8 flex flex-wrap items-center gap-3">
            {valueProps.map((v) => (
              <li
                key={v.label}
                className="inline-flex items-center gap-2 pl-1.5 pr-3.5 py-1.5 rounded-full border border-brand bg-white/80 backdrop-blur"
                data-testid={`hero-value-${v.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: v.tone.bg, color: v.tone.fg }}>
                  <v.icon className="w-3.5 h-3.5" />
                </span>
                <span className="text-sm font-bold text-brand-deep">{v.label}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* visual derecha: dashboard mockup + tarjeta institucional flotante */}
        <div ref={b.ref} className="reveal relative" style={{ transitionDelay: '120ms' }}>
          <DashboardMockup />

          {/* tarjeta flotante "Nuestro enfoque" con pilares pastel */}
          <div
            data-testid="hero-pillars-card"
            className="mt-4 sm:mt-5 rounded-2xl bg-white border border-brand p-4 sm:p-5"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-violet">Nuestro enfoque</p>
            <div className="mt-3 grid grid-cols-4 gap-3">
              {pillars.map((p) => (
                <div key={p.label} className="flex flex-col items-center text-center gap-2" data-testid={`hero-pillar-${p.label.toLowerCase()}`}>
                  <span className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: p.tone.bg, color: p.tone.fg }}>
                    <p.icon className="w-5 h-5" />
                  </span>
                  <span className="text-[11px] sm:text-xs font-semibold text-brand-deep">{p.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* mini insignia */}
          <div className="hidden sm:flex absolute -top-5 -left-5 items-center gap-2 bg-white border border-brand rounded-full px-4 py-2 shadow-md">
            <ShieldCheck className="w-4 h-4 text-brand-violet" />
            <span className="text-xs font-semibold text-brand-deep">Inteligencia preventiva escolar</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
